import { datadogRum } from '@datadog/browser-rum';
import { DateTime } from 'luxon';
import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import BodyText from '@ui/atoms/BodyText';
import Header from '@ui/atoms/Header';
import Skeleton from '@ui/atoms/Skeleton';

import RadioButton from '@ui/forms/RadioButton';

import EyebrowLink from '@ui/molecules/EyebrowLink';

import BookingUnavailable from '@ui/organisms/BookingUnavailable/BookingUnavailable.component';

import NewAppointmentBooking from '@components/NewAppointmentBooking';

import { ActionChoice, IManageAppointmentsContent } from './ManageAppointments.page';

interface IManageAppointmentsProps {
	loadingData: boolean;
	confirmError: boolean;
	confirmErrorRescheduleUrl?: string;
	pageContent: IManageAppointmentsContent;
	memberState: string | null;
	actionChoices?: ActionChoice[];
	serviceDisplayName: (serviceType: 'e' | 't' | 'prep' | 'vc' | 'ed' | 'inj') => {
		article: string;
		name: string;
	};
}

export const AppointmentTypeContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify: center;
	align-items: center;
	margin-bottom: 2.4rem;
	${EyebrowLink} {
		margin-bottom: 1.6rem;
	}
	${Header} {
		margin: 1.6rem 0;
	}
`;

export const OptionsContainer = styled.div`
	display: flex;
	flex-direction: column;
	padding: 2.4rem;
	> label {
		margin-top: 1.6rem;
	}
	> ${BodyText}:last-of-type {
		margin-top: 1.6rem;
	}
`;

export const SkeletonContainer = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	padding: 2.4rem;
	gap: 2.4rem;
`;

const ManageAppointments: FC<IManageAppointmentsProps> = ({
	loadingData,
	confirmError,
	confirmErrorRescheduleUrl,
	pageContent,
	memberState,
	actionChoices = [],
	serviceDisplayName,
}) => {
	const [selectedChoice, setSelectedChoice] = useState<ActionChoice>();

	const {
		butterPage: { confirmerrortext, iframeLoadingDisclaimer },
	} = pageContent;

	useEffect(() => {
		if (!loadingData && actionChoices.length == 1) {
			setSelectedChoice(actionChoices[0]);
		}
	}, [actionChoices, loadingData]);

	const cogsworthDivStyle = {
		margin: '2.4rem 0',
		border: '5px solid rgba(0, 0, 0, .5)',
		borderRadius: ' 5px',
		background: '#fff',
		// TODO simplify to just 780px after upvio migration is finished
		height: confirmErrorRescheduleUrl?.includes('upvio.com') ? '780px' : '600px',
	};

	if (loadingData) {
		return (
			<AppointmentTypeContainer>
				<SkeletonContainer>
					<Skeleton fullWidth height="lg" />
					<Skeleton fullWidth repeated={3} height="xxl" gap="md" structure="column" />
				</SkeletonContainer>
			</AppointmentTypeContainer>
		);
	}

	if (confirmError && confirmErrorRescheduleUrl) {
		return (
			<AppointmentTypeContainer>
				<Header level="4">{confirmerrortext}</Header>
				<iframe
					style={cogsworthDivStyle}
					title="cogsworthUrlIFrame"
					src={confirmErrorRescheduleUrl}
				/>
				<BasicText variant="fineprint"></BasicText>
			</AppointmentTypeContainer>
		);
	}

	if (!memberState) {
		datadogRum.addError(`State missing in manage appointments`);
		return (
			<AppointmentTypeContainer>
				<OptionsContainer>
					<BodyText>
						Sorry, we can't locate your state of residence! Please contact{' '}
						<a href="mailto:support@folxhealth.com">support@folxhealth.com</a> for help.
					</BodyText>
				</OptionsContainer>
			</AppointmentTypeContainer>
		);
	}

	if (actionChoices.length == 0) {
		return (
			<AppointmentTypeContainer>
				<OptionsContainer>
					<Header level="5">
						It looks like you don't have any upcoming appointments or active subscriptions.
					</Header>
					<Header level="5">
						Take a look at the different services <a href="/">we offer here</a>.
					</Header>
				</OptionsContainer>
			</AppointmentTypeContainer>
		);
	}

	const injectionTrainingLabel = (appt: any) => {
		if (appt.serviceType === 'inj') {
			return '(30 minutes) — A FOLX nurse will walk through how to inject (using supplies) and answer all your questions about injections! (Injection Trainings are most beneficial when your injection supplies have already arrived.)';
		}
		return '';
	};

	return (
		<AppointmentTypeContainer>
			<OptionsContainer>
				{
					/* Header */
					actionChoices.every((appt) => appt.action == 'schedule') ? (
						actionChoices.every((appt) => appt?.duration) ? (
							<>
								<Header level="4">Let's schedule your appointment</Header>
								<BodyText>First, select an appointment type:</BodyText>
							</>
						) : (
							<>
								<Header level="4">Let's schedule your appointment</Header>
							</>
						)
					) : (
						<>
							<Header level="4">Let’s manage your appointments</Header>
						</>
					)
				}
				{
					/* Existing appointments */
					actionChoices.map((appt) =>
						appt.action == 'reschedule' ? (
							<Header level="6" key={`reschedule-${appt.serviceType}`}>
								You currently have {serviceDisplayName(appt.serviceType).article}{' '}
								{serviceDisplayName(appt.serviceType).name} appointment scheduled for{' '}
								{appt.scheduledTime.toLocaleString({
									month: 'long',
									day: 'numeric',
								})}{' '}
								at {appt.scheduledTime.toLocaleString(DateTime.TIME_SIMPLE).toLowerCase()}.
							</Header>
						) : null
					)
				}
				{
					/* Options (if more than one) */
					actionChoices.length > 1
						? actionChoices.map((appt) =>
								appt.action == 'reschedule' ? (
									<RadioButton
										name="appointmentType"
										key={`reschedule-${appt.serviceType}`}
										value={`reschedule-${appt.serviceType}`}
										onClick={() => setSelectedChoice(appt)}
										label={`Reschedule your ${
											serviceDisplayName(appt.serviceType).name
										} appointment`}
									/>
								) : appt.duration == 'extended' ? (
									<RadioButton
										name="appointmentType"
										key={`schedule-${appt.serviceType}-${appt.duration}`}
										value={`schedule-${appt.serviceType}-${appt.duration}`}
										onClick={() => setSelectedChoice(appt)}
										label={`Schedule an extended ${
											serviceDisplayName(appt.serviceType).name
										} video visit (30 minutes) — choose this if you have a list of questions or concerns that you want to talk over fully with your clinician.`}
									/>
								) : appt.duration == 'brief' ? (
									<RadioButton
										name="appointmentType"
										key={`schedule-${appt.serviceType}-${appt.duration}`}
										value={`schedule-${appt.serviceType}-${appt.duration}`}
										onClick={() => setSelectedChoice(appt)}
										label={`Schedule a brief ${
											serviceDisplayName(appt.serviceType).name
										} video visit (15 minutes) — choose this if you want to meet your clinician and have a couple questions, but you don't have a lot you want to cover.`}
									/>
								) : (
									<RadioButton
										name="appointmentType"
										key={`schedule-${appt.serviceType}`}
										value={`schedule-${appt.serviceType}-${appt.duration}`}
										onClick={() => setSelectedChoice(appt)}
										label={`Schedule ${serviceDisplayName(appt.serviceType).article} ${
											serviceDisplayName(appt.serviceType).name
										} video visit ${injectionTrainingLabel(appt)}`}
									/>
								)
						  )
						: null
				}
				{
					/* Cogsworth */
					selectedChoice?.action == 'schedule' ? (
						<>
							<NewAppointmentBooking
								serviceType={selectedChoice.serviceType}
								serviceDuration={selectedChoice.duration}
								serviceVersion={selectedChoice.version}
							/>
							<BasicText variant="fineprint">{iframeLoadingDisclaimer}</BasicText>
						</>
					) : selectedChoice?.cogsworthUrl ? (
						<>
							<BookingUnavailable>
								<iframe
									style={cogsworthDivStyle}
									title="cogsworthUrlIFrame"
									src={selectedChoice.cogsworthUrl}
								/>
							</BookingUnavailable>
							<BasicText variant="fineprint">{iframeLoadingDisclaimer}</BasicText>
							<BodyText>
								If you need help rescheduling or canceling an appointment, our support team can
								assist. Submit a request in the{' '}
								<a href="https://support.folxhealth.com/hc/en-us">FOLX Help Center</a>.
							</BodyText>
							<BodyText>
								After you cancel an appointment, we will issue a refund for the amount you paid to
								book your appointment. Your refund will be complete within 5-10 business days.
							</BodyText>
						</>
					) : null
				}
			</OptionsContainer>
		</AppointmentTypeContainer>
	);
};

export default ManageAppointments;
